import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calculator, Palette, Globe } from "lucide-react";

const academicPrograms = [
  {
    level: "Pre-Primary",
    grades: "Nursery - UKG",
    description: "Foundation years focusing on play-based learning, social skills, and early literacy",
    icon: Palette,
    color: "bg-pink-50",
    iconColor: "text-pink-600",
  },
  {
    level: "Primary School",
    grades: "Class 1 - 5",
    description: "Building strong fundamentals in core subjects with activity-based learning",
    icon: BookOpen,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    level: "Middle School",
    grades: "Class 6 - 8",
    description: "Developing critical thinking, research skills, and subject specialization",
    icon: Calculator,
    color: "bg-green-50",
    iconColor: "text-green-600",
  },
  {
    level: "High School",
    grades: "Class 9 - 10",
    description: "Preparing for board exams with comprehensive academic and career guidance",
    icon: Globe,
    color: "bg-amber-50",
    iconColor: "text-amber-600",
  },
];

export const AcademicsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="academics-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">
            Academic Programs
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
            Nurturing Excellence at Every Stage
          </h2>
          <div className="section-divider mx-auto"></div>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Our comprehensive curriculum is designed to meet the unique developmental 
            needs of students at each academic level.
          </p>
        </motion.div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {academicPrograms.map((program, index) => (
            <motion.div
              key={program.level}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
              data-testid={`academic-program-${index}`}
            >
              <div className={`w-14 h-14 ${program.color} rounded-xl flex items-center justify-center mb-4`}>
                <program.icon className={`w-7 h-7 ${program.iconColor}`} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-1">
                {program.level}
              </h3>
              <p className="text-sm text-secondary font-medium mb-3">{program.grades}</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {program.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Link to="/academics">
            <Button className="bg-primary hover:bg-primary/90 rounded-full px-8">
              Explore All Programs
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
